import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => 
    api.post('/auth/login', { username, password }).then(res => res.data),
  
  getProfile: () => 
    api.get('/auth/profile').then(res => res.data.user),
  
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword }).then(res => res.data),
};

// Customers API
export const customersAPI = {
  getAll: (params = {}) => 
    api.get('/customers', { params }).then(res => res.data),
  
  getById: (id) => 
    api.get(`/customers/${id}`).then(res => res.data),
  
  create: (data) => 
    api.post('/customers', data).then(res => res.data),
  
  update: (id, data) => 
    api.put(`/customers/${id}`, data).then(res => res.data),
  
  search: (term) => 
    api.get(`/customers/search/${term}`).then(res => res.data),
};

// Shipments API
export const shipmentsAPI = {
  getAll: (params = {}) => 
    api.get('/shipments', { params }).then(res => res.data),
  
  getById: (id) => 
    api.get(`/shipments/${id}`).then(res => res.data),
  
  create: (data) => 
    api.post('/shipments', data).then(res => res.data),
  
  update: (id, data) => 
    api.put(`/shipments/${id}`, data).then(res => res.data),
  
  updateStatus: (id, newStatus, comment) => 
    api.patch(`/shipments/${id}/status`, { newStatus, comment }).then(res => res.data),
  
  updateShipmentStatus: (id, newStatus) => 
    api.patch(`/shipments/${id}/shipment-status`, { newShipmentStatus: newStatus }).then(res => res.data),
  
  assignUsers: (id, userIds) => 
    api.patch(`/shipments/${id}/assign`, { userIds }).then(res => res.data),
  
  addComment: (shipmentId, text) => 
    api.post(`/shipments/${shipmentId}/comments`, { text }).then(res => res.data),
  
  editComment: (shipmentId, commentId, text) => 
    api.put(`/shipments/${shipmentId}/comments/${commentId}`, { text }).then(res => res.data),
  
  deleteComment: (shipmentId, commentId) => 
    api.delete(`/shipments/${shipmentId}/comments/${commentId}`).then(res => res.data),
  
  updatePaymentStatus: (id, newStatus) => 
    api.patch(`/shipments/${id}/payment-status`, { newPaymentStatus: newStatus }).then(res => res.data),
};

// Attachments API
export const attachmentsAPI = {
  getByShipment: (shipmentId) => 
    api.get(`/attachments/shipment/${shipmentId}`).then(res => res.data),
  
  upload: (shipmentId, files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    return api.post(`/attachments/shipment/${shipmentId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },
  
  delete: (attachmentId) => 
    api.delete(`/attachments/${attachmentId}`).then(res => res.data),
  
  download: (attachmentId) => 
    api.get(`/attachments/${attachmentId}/download`).then(res => res.data),
  
  getHistory: (shipmentId) => 
    api.get(`/attachments/shipment/${shipmentId}/history`).then(res => res.data),
};

// Users API
export const usersAPI = {
  getAll: () => 
    api.get('/users').then(res => res.data),
  
  getActive: () => 
    api.get('/users/active').then(res => res.data),
  
  getByRole: (roleName) => 
    api.get(`/users/by-role/${roleName}`).then(res => res.data),
  
  getById: (id) => 
    api.get(`/users/${id}`).then(res => res.data),
  
  create: (data) => 
    api.post('/users', data).then(res => res.data),
  
  update: (id, data) => 
    api.put(`/users/${id}`, data).then(res => res.data),
  
  updatePassword: (id, password) => 
    api.patch(`/users/${id}/password`, { password }).then(res => res.data),
  
  updateStatus: (id, isActive) => 
    api.patch(`/users/${id}/status`, { isActive }).then(res => res.data),
  
  updateBlockStatus: (id, isBlocked) => 
    api.patch(`/users/${id}/block`, { isBlocked }).then(res => res.data),
  
  getStats: (id) => 
    api.get(`/users/${id}/stats`).then(res => res.data),
  
  // Roles API
  getRoles: () => 
    api.get('/users/roles').then(res => res.data),
};

// Routes API
export const routesAPI = {
  getAll: () => 
    api.get('/routes').then(res => res.data),
  create: (data) => api.post('/routes', data).then(res => res.data),
  update: (id, data) => api.put(`/routes/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/routes/${id}`).then(res => res.data),
};

// Cities API
export const citiesAPI = {
  getAll: () => 
    api.get('/cities').then(res => res.data),
  getById: (id) => 
    api.get(`/cities/${id}`).then(res => res.data),
  create: (data) => api.post('/cities', data).then(res => res.data),
  update: (id, data) => api.put(`/cities/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/cities/${id}`).then(res => res.data),
};

// Dashboard API
export const dashboardAPI = {
  getOverview: () => 
    api.get('/dashboard/overview').then(res => res.data),
  
  getByAssignee: () => 
    api.get('/dashboard/by-assignee').then(res => res.data),
  
  getByCity: () => 
    api.get('/dashboard/by-city').then(res => res.data),
  
  getMonthlyTrends: () => 
    api.get('/dashboard/monthly-trends').then(res => res.data),
  
  getPaymentStats: () => 
    api.get('/dashboard/payment-stats').then(res => res.data),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params = {}) => 
    api.get('/notifications', { params }).then(res => res.data),
  
  markAsRead: (id) => 
    api.patch(`/notifications/${id}/read`).then(res => res.data),
  
  markAllAsRead: () => 
    api.patch('/notifications/read-all').then(res => res.data),
  
  getUnreadCount: () => 
    api.get('/notifications/unread-count').then(res => res.data),
  
  delete: (id) => 
    api.delete(`/notifications/${id}`).then(res => res.data),
};

// Shipment Batches API
export const shipmentBatchesAPI = {
  getAll: (params) => api.get('/shipment-batches', { params }).then(res => res.data),
  getById: (id) => api.get(`/shipment-batches/${id}`).then(res => res.data),
  create: (data) => api.post('/shipment-batches', data).then(res => res.data),
  update: (id, data) => api.put(`/shipment-batches/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/shipment-batches/${id}`).then(res => res.data),
};

export { api }; 